import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, Trash2, GripVertical, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useStacks } from '@/lib/hooks';
import { STACK_ICONS, STACK_COLORS } from '@/lib/store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

function generateId() {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export function CreateEditStackPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { stacks, createStack, updateStack, canCreateStack, freeTierLimit, isLoading: stacksLoading } = useStacks();

    const isEditing = !!id;
    const existingStack = isEditing ? stacks.find(s => s.id === id) : null;

    const [name, setName] = useState('');
    const [icon, setIcon] = useState('sun');
    const [color, setColor] = useState('crimson');
    const [reminderTime, setReminderTime] = useState('');
    const [items, setItems] = useState([]);
    const [editingItemId, setEditingItemId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [showAllIcons, setShowAllIcons] = useState(false);

    useEffect(() => {
        if (existingStack) {
            setName(existingStack.name);
            setIcon(existingStack.icon);
            setColor(existingStack.color);
            setReminderTime(existingStack.reminder_time || '');
            setItems((existingStack.items || []).map(item => ({ ...item })));
        }
    }, [existingStack]);

    const addItem = () => {
        if (items.length >= 9) {
            toast('Stacks cap at 9 items');
            return;
        }

        const newItem = {
            id: generateId(),
            name: '',
            minimum_version: '',
            position: items.length,
        };

        setItems([...items, newItem]);
        setEditingItemId(newItem.id);
    };

    const updateItem = (itemId, updates) => {
        setItems(items.map(item =>
            item.id === itemId ? { ...item, ...updates } : item
        ));
    };

    const deleteItem = (itemId) => {
        const newItems = items.filter(item => item.id !== itemId);
        newItems.forEach((item, index) => item.position = index);
        setItems(newItems);
    };

    const handleSave = async () => {
        if (!name.trim()) {
            toast('Name required');
            return;
        }

        const validItems = items.filter(item => item.name.trim());
        if (validItems.length === 0) {
            toast('Add at least 1 item');
            return;
        }

        if (!isEditing && !canCreateStack) {
            toast(`Free tier limit: ${freeTierLimit} stacks`);
            return;
        }

        setSaving(true);

        const stackData = {
            name: name.trim(),
            icon,
            color,
            reminder_time: reminderTime || null,
            grace_days: 1,
            items: validItems.map((item, index) => ({
                name: item.name.trim(),
                minimum_version: item.minimum_version?.trim() || null,
                position: index,
            })),
        };

        try {
            if (isEditing) {
                await updateStack(id, stackData);
                toast('Saved');
            } else {
                await createStack(stackData);
                toast('Stack created');
            }
            navigate('/stacks');
        } catch (err) {
            toast.error(err.message || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    if (stacksLoading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background flex flex-col">
            {/* Header */}
            <header className="sticky top-0 bg-background border-b border-border z-10">
                <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(-1)}
                            className="-ml-2"
                        >
                            <X className="w-5 h-5" />
                        </Button>
                        <h1 className="font-semibold">
                            {isEditing ? 'Edit Stack' : 'New Stack'}
                        </h1>
                    </div>
                    <Button onClick={handleSave} size="sm" disabled={saving}>
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                    </Button>
                </div>
            </header>

            {/* Form */}
            <main className="flex-1 max-w-lg mx-auto w-full px-4 py-4">
                {/* Name */}
                <div className="mb-5">
                    <Label htmlFor="name" className="text-sm font-medium mb-1.5 block">
                        Name
                    </Label>
                    <Input
                        id="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Morning Routine"
                        autoFocus={!isEditing}
                    />
                </div>

                {/* Icon picker */}
                <div className="mb-5">
                    <Label className="text-sm font-medium mb-1.5 block">Icon</Label>
                    <div className="flex flex-wrap gap-1.5">
                        {STACK_ICONS.slice(0, showAllIcons ? STACK_ICONS.length : 33).map((i) => (
                            <button
                                key={i.name}
                                onClick={() => setIcon(i.name)}
                                className={cn(
                                    "w-9 h-9 rounded-lg flex items-center justify-center text-lg transition-all",
                                    icon === i.name
                                        ? "bg-primary/10 ring-2 ring-primary"
                                        : "bg-muted hover:bg-muted/80"
                                )}
                                title={i.label}
                            >
                                {i.emoji}
                            </button>
                        ))}
                    </div>
                    {!showAllIcons && STACK_ICONS.length > 33 && (
                        <button
                            onClick={() => setShowAllIcons(true)}
                            className="mt-2 text-sm text-primary hover:underline"
                        >
                            Show more icons ({STACK_ICONS.length - 33} more)
                        </button>
                    )}
                    {showAllIcons && (
                        <button
                            onClick={() => setShowAllIcons(false)}
                            className="mt-2 text-sm text-muted-foreground hover:underline"
                        >
                            Show less
                        </button>
                    )}
                </div>

                {/* Color picker */}
                <div className="mb-5">
                    <Label className="text-sm font-medium mb-1.5 block">Color</Label>
                    <div className="flex flex-wrap gap-1.5">
                        {STACK_COLORS.map((c) => (
                            <button
                                key={c.name}
                                onClick={() => setColor(c.name)}
                                className={cn(
                                    "w-9 h-9 rounded-lg transition-all",
                                    c.class,
                                    color === c.name && "ring-2 ring-offset-2 ring-foreground"
                                )}
                                title={c.label}
                            />
                        ))}
                    </div>
                </div>

                {/* Reminder time */}
                <div className="mb-6">
                    <Label htmlFor="reminder" className="text-sm font-medium mb-1.5 block">
                        Reminder (optional)
                    </Label>
                    <Input
                        id="reminder"
                        type="time"
                        value={reminderTime}
                        onChange={(e) => setReminderTime(e.target.value)}
                        className="w-36"
                    />
                </div>

                {/* Items */}
                <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                        <Label className="text-sm font-medium">Items</Label>
                        <span className="text-xs text-muted-foreground">
                            {items.length}/9
                        </span>
                    </div>

                    <div className="space-y-2 mb-3">
                        {items
                            .sort((a, b) => a.position - b.position)
                            .map((item) => (
                                <div key={item.id} className="flex items-start gap-2 p-2 bg-muted rounded-lg">
                                    <GripVertical className="w-4 h-4 text-muted-foreground mt-2.5 shrink-0" />

                                    <div className="flex-1 space-y-1.5">
                                        <Input
                                            value={item.name}
                                            onChange={(e) => updateItem(item.id, { name: e.target.value })}
                                            placeholder="Habit name"
                                            className="h-8 text-sm"
                                            autoFocus={editingItemId === item.id}
                                        />
                                        <Input
                                            value={item.minimum_version || ''}
                                            onChange={(e) => updateItem(item.id, { minimum_version: e.target.value })}
                                            placeholder="Min version (e.g. 'just 2 mins')"
                                            className="h-7 text-xs"
                                        />
                                    </div>

                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => deleteItem(item.id)}
                                        className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            ))}
                    </div>

                    <Button
                        variant="outline"
                        onClick={addItem}
                        disabled={items.length >= 9}
                        className="w-full gap-1.5 h-9"
                    >
                        <Plus className="w-4 h-4" />
                        Add item
                    </Button>

                    {items.length >= 9 && (
                        <p className="text-xs text-muted-foreground text-center mt-2">
                            Stacks cap at 9 items. Keep it small.
                        </p>
                    )}
                </div>
            </main>

            {/* Sticky save */}
            <div className="sticky bottom-0 bg-background border-t border-border p-4">
                <div className="max-w-lg mx-auto">
                    <Button onClick={handleSave} className="w-full h-11" disabled={saving}>
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (isEditing ? 'Save Changes' : 'Create Stack')}
                    </Button>
                </div>
            </div>
        </div>
    );
}
